import React, { useCallback, useMemo } from 'react';
import { Lead, CrmUser } from '../../../../types';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { TagSelector } from './TagSelector';
import { formatDateTime } from '../../../../utils/formatters';
import { MarkdownEditor } from './MarkdownEditor';
import { generateSummaryForLead } from '../../../../services/geminiService';
import { notify } from '../../../../utils/toast';
import { EditableField, EditableSelect } from '../../../../components/shared/InlineEditable';
import { usePermissions } from '../../../../hooks/usePermissions';

interface DealInfoSectionProps {
    lead: Lead;
    crmUsers: CrmUser[];
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    onObservationsDraftChange?: (html: string) => void;
}

const rowClass = 'group flex items-start gap-s3 md:gap-s4';
const iconClass = 'mt-s1 size-5 shrink-0 text-muted';

export const DealInfoSection: React.FC<DealInfoSectionProps> = ({
    lead,
    crmUsers,
    onInputChange,
    onObservationsDraftChange,
}) => {
    const { canViewAllLeads } = usePermissions();

    const triggerInputChange = useCallback((name: string, value: string) => {
        onInputChange({
            target: { name, value },
        } as React.ChangeEvent<HTMLInputElement>);
    }, [onInputChange]);

    const handleValueSave = useCallback((val: string | number) => {
        const num = typeof val === 'number' ? val : parseFloat(String(val)) || 0;
        triggerInputChange('value', String(num));
    }, [triggerInputChange]);

    const handleAgentSave = useCallback(async (agentId: string) => {
        triggerInputChange('agent', agentId);
    }, [triggerInputChange]);

    const handleTagsChange = useCallback((tagIds: string[]) => {
        triggerInputChange('tags', JSON.stringify(tagIds));
    }, [triggerInputChange]);

    const agentDisplay = useMemo(() => {
        if (!lead.agent) return 'Ninguém';
        return crmUsers.find((u) => u.id === lead.agent)?.name ?? 'Ninguém';
    }, [lead.agent, crmUsers]);

    const agentOptions = useMemo(
        () => [
            { value: '', label: 'Ninguém' },
            ...crmUsers.map((u) => ({ value: u.id, label: u.name })),
        ],
        [crmUsers],
    );

    const creatorDisplay = useMemo(() => {
        if (lead.creatorName) return lead.creatorName;
        if (lead.createdBy) {
            return crmUsers.find((u) => u.id === lead.createdBy)?.name ?? 'Membro removido';
        }
        return null;
    }, [lead.createdBy, lead.creatorName, crmUsers]);

    return (
        <fieldset className="m-0 space-y-s4 border-t border-border p-0 pt-s5 md:space-y-s5">
            <legend className="sr-only">Dados da negociação</legend>

            <div className={rowClass}>
                <AppIcon name="dollar" className={iconClass} aria-hidden />
                <div className="min-w-0 flex-1">
                    <EditableField
                        label="Valor"
                        value={lead.value}
                        type="currency"
                        onSave={handleValueSave}
                    />
                </div>
            </div>

            <div className={rowClass}>
                <AppIcon name="id" className={iconClass} aria-hidden />
                <div className="min-w-0 flex-1">
                    <span className="mb-s1 block text-caption text-muted">Responsável</span>
                    <EditableSelect
                        value={lead.agent || ''}
                        displayValue={agentDisplay}
                        options={agentOptions}
                        placeholder="Ninguém"
                        editTitle="Clique para editar o responsável"
                        onSave={handleAgentSave}
                    />
                </div>
            </div>

            {canViewAllLeads && creatorDisplay ? (
                <div className={rowClass}>
                    <AppIcon name="user" className={iconClass} aria-hidden />
                    <div className="min-w-0 flex-1">
                        <span className="mb-s1 block text-caption text-muted">Criado por</span>
                        <span className="text-base text-default">{creatorDisplay}</span>
                    </div>
                </div>
            ) : null}

            <div className={rowClass}>
                <AppIcon name="tag" className={iconClass} aria-hidden />
                <div className="min-w-0 flex-1">
                    <TagSelector
                        leadId={lead.id}
                        selectedTags={lead.tags || []}
                        onTagsChange={handleTagsChange}
                    />
                </div>
            </div>

            <div className="group flex items-center gap-s3 md:gap-s4">
                <AppIcon name="calendar" className="size-5 shrink-0 text-muted" aria-hidden />
                <time
                    className="font-mono text-base tabular-nums text-default"
                    dateTime={lead.entryDate}
                >
                    {formatDateTime(lead.entryDate)}
                </time>
            </div>

            <div className="pt-s2">
                <MarkdownEditor
                    initialValue={lead.observations || ''}
                    onSave={(val) => triggerInputChange('observations', val)}
                    onDraftChange={onObservationsDraftChange}
                    maxLength={5000}
                    onGenerateAISummary={async () => {
                        try {
                            return await generateSummaryForLead(lead);
                        } catch (error: unknown) {
                            const message =
                                error instanceof Error
                                    ? error.message
                                    : 'Falha ao analisar a negociação com IA.';
                            notify.error(message);
                            throw error;
                        }
                    }}
                />
            </div>
        </fieldset>
    );
};
